import { useMutation, UseMutationOptions } from '@tanstack/react-query'
import { mergeDeepLeft } from 'ramda'

import { basicFetch } from './basicFetch'
import {
  convertEndpointProviderToFetchArgs,
  EndpointProviderDefaults,
} from './convertEndpointProviderToFetchArgs'
import { EndpointInterface } from './Endpoint'
import { createAuthHeaders } from './headers'
import { defaultErrorBoundaryHandler } from './helpers/defaultErrorBoundaryHandler'
import { useNetworkContext } from './NetworkProvider'

type ExtractGeneric<Type> = Type extends EndpointInterface<infer X> ? X : never

type EndpointProviderFactory<T = any> = (...args: any) => EndpointInterface<T>

type EndpointReturnType<T> = T extends EndpointProviderFactory<infer R>
  ? R
  : unknown

type UseMutationEndpointOptions<TResponse, TBuilderParams> = Partial<{
  opts: UseMutationOptions<TResponse, unknown, TBuilderParams>
  defaults: EndpointProviderDefaults
}>

export type UseMutationOptionsFromProvider<
  EndpointFactory extends EndpointProviderFactory<any>,
  TBuilderParams extends any,
> = UseMutationEndpointOptions<
  EndpointReturnType<EndpointFactory>,
  TBuilderParams
>

// TODO: add type for error
export function useMutationEndpoint<
  TBuilder extends (variables: any) => EndpointInterface,
  TBuilderParams extends Parameters<TBuilder>[0],
  TBuilderResponse extends ReturnType<TBuilder>,
  TResponse extends ExtractGeneric<TBuilderResponse>,
>(
  builder: TBuilder,
  {
    opts,
    defaults,
  }: UseMutationEndpointOptions<TResponse, TBuilderParams> = {},
) {
  const { token, version, baseUrl, errorBoundaryHandler, platform } =
    useNetworkContext()

  return useMutation<TResponse, unknown, TBuilderParams>({
    mutationFn: (variables) =>
      builder(variables)((data) =>
        basicFetch(
          ...convertEndpointProviderToFetchArgs(
            mergeDeepLeft(defaults || {}, {
              baseUrl,
              version,
              platform,
              headers: token ? createAuthHeaders(token) : undefined,
            }),
          )(data as any),
        ),
      ),
    ...opts,

    throwOnError:
      opts?.throwOnError ??
      (errorBoundaryHandler || defaultErrorBoundaryHandler),
  })
}

// EXAMPLE:
// const mutation = useMutationEndpoint(
//     (password: string) => setPassword({ password }),
//     {
//       defaults: {
//         version: config.appVersion,
//         headers: createAuthHeaders(token || ''),
//       },
//     },
//   )
